export type LandingCurrency = 'EUR' | 'USD';

/** Euro-area ISO 3166-1 alpha-2 codes (matches backend walletCurrency launch set). */
const EUROZONE_COUNTRIES = new Set([
    'AT', 'BE', 'CY', 'DE', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'IE', 'IT',
    'LT', 'LU', 'LV', 'MT', 'NL', 'PT', 'SI', 'SK',
]);

/** Non-euro EU and broader European markets shown EUR on the landing page. */
const EUROPE_EUR_COUNTRIES = new Set([
    ...EUROZONE_COUNTRIES,
    'BG', 'CZ', 'DK', 'GB', 'HU', 'IS', 'LI', 'NO', 'PL', 'RO', 'SE', 'CH',
]);

export const LANDING_CURRENCY_FALLBACK: LandingCurrency = 'EUR';

export function normalizeCountryCode(country: string | null | undefined): string | null {
    if (!country) {
        return null;
    }
    const trimmed = country.trim();
    if (!trimmed) {
        return null;
    }
    return trimmed.toUpperCase();
}

/**
 * Maps a detected country to landing-page display currency.
 * EU/Europe visitors see EUR; all other regions see USD.
 * Unknown country defaults to EUR (EU-focused product default, aligned with backend wallet fallback).
 */
export function mapCountryToLandingCurrency(countryCode: string | null | undefined): LandingCurrency {
    const normalized = normalizeCountryCode(countryCode);
    if (!normalized) {
        return LANDING_CURRENCY_FALLBACK;
    }
    if (EUROPE_EUR_COUNTRIES.has(normalized)) {
        return 'EUR';
    }
    return 'USD';
}

function getGeolocationApiUrl(): string {
    if (import.meta.env.DEV) {
        return '';
    }
    const isBlog = typeof window !== 'undefined' && window.location.hostname.startsWith('blog.');
    return import.meta.env.VITE_API_URL || (isBlog ? 'https://minifil.com' : 'http://localhost:3001');
}

export async function fetchLandingCurrencyFromGeolocation(): Promise<LandingCurrency> {
    const apiUrl = getGeolocationApiUrl();
    const response = await fetch(`${apiUrl}/api/geolocation`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        throw new Error(`Geolocation API returned ${response.status}`);
    }

    const data: { countryCode?: string | null } = await response.json();
    return mapCountryToLandingCurrency(data.countryCode);
}
